/** Manage one source's explicit record scope without changing connector settings. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, TriangleAlert, Upload } from 'lucide-react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/services'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@/components/ui'
import { getWorkspaceVaultPairing } from '@/subsystems/vault/services/vault-api'
import { waitForVaultJob } from '@/subsystems/vault/services/vault-jobs'
import {
  getConnectorPullCatalog,
  listConnectorManagedRecords,
  pullConnectorRecords,
} from '../api'
import { connectorDefinition } from '../registry'
import type { ConnectorPendingAction } from '../types'
import { ManagedRecordsTab } from './ManagedRecordsTab'
import { useManagedRecordActions } from './useManagedRecordActions'

const MAX_BATCH = 50

const parseIdentifiers = (value: string) => [
  ...new Set(value.split(/[\n,]/).map(item => item.trim()).filter(Boolean)),
]

const csvFirstColumn = (value: string) => {
  const cells = value.split(/\r?\n/).map(row => row.split(',')[0].trim()).filter(Boolean)
  if (/^(id|identifier|record id|sys_id|award number|subaward id|incident number)$/i.test(cells[0] ?? '')) cells.shift()
  return cells.join('\n')
}

export function ManageRecordsDialog({
  api,
  orgId,
  workspaceId,
  feedId,
  open,
  onOpenChange,
  onQueued,
  onPendingChange,
}: {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  feedId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onQueued?: () => void | Promise<unknown>
  onPendingChange?: (feedId: string, action?: ConnectorPendingAction) => void
}) {
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [tab, setTab] = useState<'add' | 'list'>('add')
  const [selectedFeedId, setSelectedFeedId] = useState('')
  const [recordTypes, setRecordTypes] = useState<string[]>([])
  const [value, setValue] = useState('')
  const [loadedFromCsv, setLoadedFromCsv] = useState(false)
  const [syncingBatch, setSyncingBatch] = useState(false)
  const pairing = useQuery({
    queryKey: ['workspace-vault-pairing', orgId, workspaceId],
    queryFn: () => getWorkspaceVaultPairing(api, orgId, workspaceId),
    enabled: open && Boolean(orgId && workspaceId),
  })
  const catalog = useQuery({
    queryKey: ['connector-pull-catalog', orgId, pairing.data?.vaultId],
    queryFn: () => getConnectorPullCatalog(api, orgId, pairing.data!.vaultId),
    enabled: open && Boolean(pairing.data?.vaultId),
  })
  const sources = catalog.data?.sources ?? []
  const source = feedId
    ? sources.find(item => item.feedId === feedId)
    : sources.find(item => item.feedId === selectedFeedId) ?? sources[0]
  const managedKey = [
    'connector-managed-records',
    orgId,
    pairing.data?.vaultId,
    source?.feedId,
  ] as const
  const managed = useQuery({
    queryKey: managedKey,
    queryFn: () => listConnectorManagedRecords(
      api,
      orgId,
      pairing.data!.vaultId,
      source!.feedId,
    ),
    enabled: open && Boolean(pairing.data?.vaultId && source?.feedId),
    refetchInterval: open && tab === 'list' ? 2_000 : false,
  })
  const availableTypes = source?.recordTypes ?? []
  const selectedTypes = availableTypes.filter(type => recordTypes.includes(type.recordType))
  const activeTypes = selectedTypes.length > 0 ? selectedTypes : availableTypes.slice(0, 1)
  const identifiers = useMemo(() => parseIdentifiers(value), [value])
  const overLimit = identifiers.length > MAX_BATCH
  const overBy = identifiers.length - MAX_BATCH
  const connectorName = source ? connectorDefinition(source.provider).label : ''
  const recordUnit = source?.recordUnit ?? { singular: 'record', plural: 'records' }
  const recordUnitSentence = recordUnit.plural.replace(/^./, character => character.toUpperCase())
  const estimatedResults = source
    ? Math.round(identifiers.length * source.estimatedResultsPerIdentifierPerType * activeTypes.length)
    : 0
  const records = managed.data ?? []
  const managedActions = useManagedRecordActions({
    api,
    orgId,
    workspaceId,
    vaultId: pairing.data?.vaultId,
    source,
    records,
    managedKey,
    recordUnit,
    onQueued,
    onPendingChange,
  })

  useEffect(() => {
    if (!open) return
    setTab('add')
    setSelectedFeedId(feedId ?? '')
    setRecordTypes([])
    setValue('')
    setLoadedFromCsv(false)
  }, [feedId, open])

  const toggleRecordType = (recordType: string) => {
    const current = activeTypes.map(type => type.recordType)
    setRecordTypes(current.includes(recordType)
      ? current.length > 1 ? current.filter(item => item !== recordType) : current
      : [...current, recordType])
  }

  const syncRecords = async () => {
    if (!pairing.data || !source || activeTypes.length === 0 || identifiers.length === 0 || overLimit) return
    setSyncingBatch(true)
    onPendingChange?.(source.feedId, 'pull')
    onOpenChange(false)
    try {
      const job = await pullConnectorRecords(
        api,
        orgId,
        pairing.data.vaultId,
        source.feedId,
        activeTypes.map(type => type.recordType),
        identifiers,
      )
      const syncedLabel = identifiers.length === 1
        ? recordUnit.singular
        : recordUnit.plural
      await queryClient.invalidateQueries({ queryKey: managedKey })
      await onQueued?.()
      await waitForVaultJob(api, orgId, workspaceId, job.jobId)
      await onQueued?.()
      toast.success(`Synced ${identifiers.length} ${syncedLabel}`)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? `Could not sync ${recordUnit.plural}`)
    } finally {
      onPendingChange?.(source.feedId)
      setSyncingBatch(false)
    }
  }

  const actionLabel = identifiers.length === 0 || activeTypes.length === 0 || overLimit
    ? `Sync ${recordUnit.plural}`
    : `Sync ${identifiers.length} ${identifiers.length === 1 ? recordUnit.singular : recordUnit.plural}`
  return (
    <>
    <Dialog open={open} onOpenChange={next => { if (!syncingBatch) onOpenChange(next) }}>
      <DialogContent data-testid="manage-records-dialog" className="flex max-h-[90vh] flex-col gap-0 overflow-hidden rounded-[14px] border-0 p-0 shadow-[0_32px_80px_rgba(15,23,42,0.3)] sm:max-w-[560px] [&>[data-slot=dialog-close]]:right-[18px] [&>[data-slot=dialog-close]]:top-[18px] [&>[data-slot=dialog-close]]:grid [&>[data-slot=dialog-close]]:size-7 [&>[data-slot=dialog-close]]:place-items-center [&>[data-slot=dialog-close]]:rounded-[7px] [&>[data-slot=dialog-close]]:opacity-100 [&>[data-slot=dialog-close]]:hover:bg-slate-100 [&>[data-slot=dialog-close]>svg]:size-[15px]">
        <DialogHeader className="gap-0.5 border-b border-slate-100 px-[22px] pb-3.5 pr-14 pt-[18px] text-left">
          <DialogTitle className="text-[15.5px] font-[650]">
            {feedId && connectorName ? `Manage ${recordUnit.plural} — ${connectorName}` : 'Manage records from a source'}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] leading-[18px]">
            {recordUnitSentence} you add stay in sync — resync or unsync them any time from Currently syncing. Connection and module settings live in Configure.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-4 border-b border-slate-100 px-[22px] pt-2.5">
          <button type="button" data-testid="managed-records-tab-add" className={`border-b-2 px-0.5 pb-[9px] pt-1 text-[12.5px] font-semibold ${tab === 'add' ? 'border-slate-800 text-slate-900' : 'border-transparent text-slate-500'}`} onClick={() => setTab('add')}>
            Add to sync
          </button>
          <button type="button" data-testid="managed-records-tab-list" className={`flex items-center gap-1.5 border-b-2 px-0.5 pb-[9px] pt-1 text-[12.5px] font-semibold ${tab === 'list' ? 'border-slate-800 text-slate-900' : 'border-transparent text-slate-500'}`} onClick={() => setTab('list')}>
            Currently syncing
            <span className="rounded-full bg-slate-100 px-[7px] py-px text-[10.5px] font-semibold text-slate-500">{records.length}</span>
          </button>
        </div>
        {tab === 'add' ? (
          <div className="flex flex-col gap-4 overflow-y-auto px-[22px] pb-[18px] pt-4">
            {!feedId && (
              <section className="space-y-1.5">
                <h3 className="text-xs font-semibold text-slate-700">Source</h3>
                {catalog.isPending || pairing.isPending ? (
                  <p className="text-sm text-slate-500">Loading connected sources…</p>
                ) : sources.length === 0 ? (
                  <p className="rounded-lg border border-dashed p-3 text-sm text-slate-500">No connected source supports record management.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {sources.map(item => {
                      const definition = connectorDefinition(item.provider)
                      const selected = item.feedId === source?.feedId
                      return (
                        <button key={item.feedId} type="button" className={`flex items-center gap-2 rounded-[9px] bg-white px-3 py-2.5 text-left text-[12.5px] font-semibold ${selected ? 'border-[1.5px] border-slate-800' : 'border border-slate-200'}`} onClick={() => {
                          setSelectedFeedId(item.feedId)
                          setRecordTypes(item.recordTypes.slice(0, 1).map(type => type.recordType))
                        }}>
                          <definition.SourceIcon className="size-3.5 text-slate-600" />
                          <span className="truncate">{definition.label}</span>
                          <span className="ml-auto size-1.5 shrink-0 rounded-full bg-green-500" />
                        </button>
                      )
                    })}
                  </div>
                )}
              </section>
            )}
            {source ? (
              <>
                <section className="space-y-1.5">
                  <h3 className="text-xs font-semibold text-slate-700">
                    Types to sync{' '}
                    <span className="font-normal text-slate-400">— pick one or more; the identifiers apply to every selected type</span>
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {availableTypes.map(type => {
                      const selected = activeTypes.some(item => item.recordType === type.recordType)
                      return <button key={type.recordType} type="button" className={`h-[30px] rounded-lg border px-3 text-xs font-semibold ${selected ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-600'}`} onClick={() => toggleRecordType(type.recordType)}>{type.label}</button>
                    })}
                  </div>
                </section>
                <section className="space-y-1.5">
                  <div className="flex items-center">
                    <h3 className="text-xs font-semibold text-slate-700">
                      Identifiers{' '}
                      <span data-testid="manage-records-identifier-count" className={`font-normal ${overLimit ? 'text-amber-700' : 'text-slate-400'}`}>
                        · {overLimit ? `${identifiers.length} loaded — ${overBy} over the limit` : `${identifiers.length} loaded · max ${MAX_BATCH} per sync`}
                      </span>
                    </h3>
                    <input ref={fileInput} type="file" accept=".csv,text/csv" aria-label="Load CSV" className="hidden" onChange={event => {
                      const file = event.target.files?.[0]
                      if (!file) return
                      const reader = new FileReader()
                      reader.onload = () => {
                        setValue(csvFirstColumn(String(reader.result ?? '')))
                        setLoadedFromCsv(true)
                      }
                      reader.readAsText(file)
                      event.target.value = ''
                    }} />
                    <Button type="button" size="sm" variant="outline" className="ml-auto h-7 px-2.5 text-[11.5px]" onClick={() => fileInput.current?.click()}><Upload className="mr-1.5 size-3" /> Load CSV</Button>
                  </div>
                  <Textarea data-testid="manage-records-identifiers" value={value} placeholder={`Paste ${source.identifierLabel.toLowerCase()}`} onChange={event => {
                    setValue(event.target.value)
                    setLoadedFromCsv(false)
                  }} className="h-[92px] resize-none rounded-[9px] px-3 py-2.5 font-mono text-[12.5px] leading-[1.6]" />
                  {overLimit && (
                    <div data-testid="manage-records-batch-warning" className="flex items-start gap-2 rounded-[9px] border border-amber-200 bg-amber-50 px-3 py-2.5">
                      <TriangleAlert className="mt-0.5 size-[13px] shrink-0 text-amber-600" />
                      <p className="min-w-0 flex-1 text-[11.5px] leading-[1.55] text-amber-900">
                        {loadedFromCsv ? 'This CSV loaded' : 'This batch has'} {identifiers.length} {recordUnit.plural} — syncs are capped at {MAX_BATCH}. Split the CSV into batches of {MAX_BATCH}, and test with a small batch before the full run.
                      </p>
                      <button type="button" className="h-[26px] shrink-0 rounded-[7px] border border-amber-300 bg-white px-2.5 text-[11px] font-semibold text-amber-900 hover:bg-amber-100" onClick={() => setValue(identifiers.slice(0, MAX_BATCH).join('\n'))}>
                        Keep first {MAX_BATCH}
                      </button>
                    </div>
                  )}
                  <p className="text-[11.5px] leading-[1.45] text-slate-400">{source.identifierLabel} — one per line or comma-separated. Loading a CSV replaces the values with its first column. Sync only imports records into Vault; approve AI analysis separately from the dashboard.</p>
                </section>
              </>
            ) : feedId && (catalog.isPending || pairing.isPending) ? (
              <p className="text-sm text-slate-500">Loading connector…</p>
            ) : feedId ? (
              <p className="rounded-lg border border-dashed p-3 text-sm text-slate-500">This connector does not support record management.</p>
            ) : null}
          </div>
        ) : (
          <ManagedRecordsTab
            recordUnit={recordUnit}
            records={records}
            loading={managed.isPending}
            failed={managed.isError}
            removing={managedActions.removing}
            syncing={managedActions.syncing}
            resyncing={managedActions.resyncing}
            onSync={selected => void managedActions.syncRecords(selected)}
            onResyncAll={() => void managedActions.resyncAll()}
            onUnsync={selected => void managedActions.unsyncRecords(selected)}
          />
        )}
        <DialogFooter className="gap-3 border-t border-slate-100 bg-slate-50/80 px-[22px] py-[13px] sm:items-center">
          {tab === 'add' ? (
            <>
              <span className="mr-auto text-xs text-slate-500">{identifiers.length === 0 ? 'No identifiers yet — paste them or load a CSV' : overLimit ? `${identifiers.length} identifiers — ${overBy} over the batch limit` : `${identifiers.length} identifier${identifiers.length === 1 ? '' : 's'}${availableTypes.length > 1 && activeTypes.length > 0 ? ` · ${activeTypes.map(type => type.label.toLowerCase()).join(' + ')}` : ''}${source ? ` · est. ≈${estimatedResults} ${source.resultLabel}` : ''}`}</span>
              <Button className="h-[33px] rounded-lg px-3.5 text-[12.5px] font-semibold" variant="outline" onClick={() => onOpenChange(false)} disabled={syncingBatch}>Cancel</Button>
              <Button data-testid="manage-records-sync" className="h-[33px] rounded-lg px-[15px] text-[12.5px] font-semibold" onClick={() => {
                void syncRecords()
              }} disabled={!source || activeTypes.length === 0 || identifiers.length === 0 || overLimit || syncingBatch}>
                {syncingBatch && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}{actionLabel}
              </Button>
            </>
          ) : (
            <>
              <span className="mr-auto text-xs text-slate-500">{records.length} {recordUnit.plural} syncing · sync and unsync apply immediately</span>
              <Button className="h-[33px] rounded-lg px-[15px] text-[12.5px] font-semibold" onClick={() => onOpenChange(false)}>Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
